import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

/**
 * SEC-003 — Migration JWT localStorage → HttpOnly cookies (19/05/2026)
 * ====================================================================
 * Le JWT n'est plus stocké côté JavaScript. Il transite via :
 *   - Cookie `baobab_session` (HttpOnly, Secure en prod, SameSite=strict/lax)
 *   - Cookie `baobab_refresh` (HttpOnly, scope /api/auth)
 * Ces cookies sont posés par le backend sur /api/auth/login,
 * /api/auth/admin/login, /api/auth/refresh-token.
 *
 * `withCredentials: true` indique à axios d'envoyer automatiquement les cookies
 * cross-origin (l'API est sur un sous-domaine .preview.emergentagent.com).
 *
 * `baobab_user` reste en localStorage UNIQUEMENT pour l'UX (affichage rapide nom/role
 * au refresh, avant que /auth/me ne réponde). Cette donnée n'est PAS sensible et
 * sera de toute façon revalidée par /auth/me au boot.
 */

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Envoyer les cookies sur TOUTES les requêtes axios (cross-site via Set-Cookie HttpOnly)
axios.defaults.withCredentials = true;

// SEC-012 : Helper pour lire un cookie côté JS (le cookie csrf_token est NON-HttpOnly)
const readCookie = (name) => {
  try {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (_) { return null; }
};

// SEC-003 + SEC-012 : Interceptor REQUEST partagé
//   1. neutralise les `Authorization: Bearer null/undefined` hérités (cookie HttpOnly prend le relais)
//   2. ajoute `X-CSRF-Token` (double-submit) + `X-Requested-With:XMLHttpRequest`
//      (exigé par dependencies._extract_bearer_token côté backend)
const authRequestInterceptor = (config) => {
  // (1) Bearer null cleanup
  const auth = config?.headers?.Authorization || config?.headers?.authorization;
  if (typeof auth === 'string') {
    const trimmed = auth.replace(/^Bearer\s*/i, '').trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
      if (config.headers) {
        delete config.headers.Authorization;
        delete config.headers.authorization;
      }
    }
  }
  // (2) CSRF + X-Requested-With sur méthodes mutables uniquement
  const method = (config.method || 'get').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    const csrfToken = readCookie('csrf_token');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
};

axios.interceptors.request.use(authRequestInterceptor);

// Monkey-patch axios.create : les instances créées via axios.create() N'HÉRITENT PAS
// des interceptors du client global. On wrap axios.create pour injecter
// automatiquement l'interceptor d'auth + withCredentials sur toute nouvelle instance.
// Sinon, les ~20 axios.create() du codebase (LoadingNoteWizard, BaobabOperatorReviewFlow,
// SiteRepFlow, DriverFlow, PaperBonFlow, SignableNoteCard, InternalNotesTab,
// PublicSignPage, etc.) renvoient 403 sur leurs mutations en mode cookie.
if (!axios.__authPatched) {
  const originalCreate = axios.create.bind(axios);
  axios.create = (cfg = {}) => {
    const instance = originalCreate({ withCredentials: true, ...cfg });
    instance.interceptors.request.use(authRequestInterceptor);
    return instance;
  };
  axios.__authPatched = true;
}

// SEC-Fix 22/05/2026 — Patch global window.fetch pour injecter automatiquement
// le header X-CSRF-Token sur les requêtes mutables (POST/PUT/PATCH/DELETE).
// Sans ce patch, les appels fetch() bruts du codebase (SystemConfiguration,
// WebhooksIntegrations, TruckDatabaseConfigPanel, etc.) sont rejetés en 403
// "CSRF token invalide ou manquant" par middleware/csrf.py.
// 
// Symptôme user : "tous les paramètres n'arrivent pas à se sauvegarder".
// Root cause : axios a un intercepteur centralisé qui ajoute CSRF, mais
// fetch() le contourne. Plutôt que de migrer 9 fichiers vers axios,
// on patche fetch() pour qu'il bénéficie du même comportement.
if (typeof window !== 'undefined' && window.fetch && !window.fetch.__csrfPatched) {
  const originalFetch = window.fetch.bind(window);
  const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
  const FETCH_API_PREFIX = '/api/';

  window.fetch = (input, init = {}) => {
    try {
      const method = (init.method || 'GET').toUpperCase();
      // Construire l'URL pour détecter same-origin + path /api/*
      let urlStr = typeof input === 'string' ? input : (input && input.url) || '';
      let isApi = false;
      try {
        const u = new URL(urlStr, window.location.origin);
        isApi = u.pathname.startsWith(FETCH_API_PREFIX);
      } catch (_) {
        isApi = urlStr.includes(FETCH_API_PREFIX);
      }

      if (MUTATING_METHODS.has(method) && isApi) {
        const headers = new Headers(init.headers || {});
        // Nettoie le sentinel Bearer cookie-auth (laisse passer le cookie HttpOnly)
        const authHdr = headers.get('Authorization') || headers.get('authorization');
        if (authHdr) {
          const trimmed = authHdr.replace(/^Bearer\s*/i, '').trim();
          if (!trimmed || trimmed === 'null' || trimmed === 'undefined' || trimmed === 'cookie-auth') {
            headers.delete('Authorization');
            headers.delete('authorization');
          }
        }
        // Injecte X-CSRF-Token + X-Requested-With (idem axios interceptor)
        if (!headers.has('X-CSRF-Token')) {
          const csrfToken = readCookie('csrf_token');
          if (csrfToken) headers.set('X-CSRF-Token', csrfToken);
        }
        if (!headers.has('X-Requested-With')) {
          headers.set('X-Requested-With', 'XMLHttpRequest');
        }
        init = { ...init, headers, credentials: init.credentials || 'include' };
      } else if (isApi && !init.credentials) {
        // Pour GET on inclut quand même les cookies (sinon /auth/me etc. fail)
        init = { ...init, credentials: 'include' };
      }
    } catch (_) { /* fail-safe : ne jamais casser fetch */ }
    return originalFetch(input, init);
  };
  window.fetch.__csrfPatched = true;
}

// Refresh 5 min avant expiration
const REFRESH_MARGIN_SECONDS = 300;

// Helper : nettoyer toute trace localStorage héritée (anciennes versions)
const cleanupLegacyTokens = () => {
  try {
    localStorage.removeItem('baobab_token');
    localStorage.removeItem('baobab_refresh_token');
    localStorage.removeItem('baobab_token_expiry');
    delete axios.defaults.headers.common['Authorization'];
  } catch (_) { /* noop */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const refreshTimeoutRef = useRef(null);

  // Logout — clears local UX state + asks backend to clear cookies & bump tokenVersion
  const handleLogout = useCallback(() => {
    cleanupLegacyTokens();
    localStorage.removeItem('baobab_user');
    setUser(null);
    setIsAuthenticated(false);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
  }, []);

  // Refresh — relies on HttpOnly cookie (no token in JS)
  const refreshToken = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/auth/refresh-token`, {});
      const { expiresIn } = response.data || {};
      if (expiresIn) {
        const nextRefreshIn = (expiresIn - REFRESH_MARGIN_SECONDS) * 1000;
        if (nextRefreshIn > 0) {
          refreshTimeoutRef.current = setTimeout(refreshToken, nextRefreshIn);
        }
      }
      return true;
    } catch (error) {
      logger.debug('Token refresh failed:', error?.message);
      return false;
    }
  }, []);

  const scheduleTokenRefresh = useCallback((expiresIn) => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    const refreshIn = (expiresIn - REFRESH_MARGIN_SECONDS) * 1000;
    if (refreshIn > 0) {
      refreshTimeoutRef.current = setTimeout(refreshToken, refreshIn);
    }
  }, [refreshToken]);

  useEffect(() => {
    // 401 interceptor : try refresh once, then logout if still failing
    let consecutive401Count = 0;
    const interceptor = axios.interceptors.response.use(
      (response) => { consecutive401Count = 0; return response; },
      async (error) => {
        // 🔧 Normalisation globale du `detail` : si detail est un objet (HTTP 423 FNE_LOCKED, 422, etc.),
        // on extrait `.message` pour éviter "Objects are not valid as a React child"
        if (error.response?.data?.detail && typeof error.response.data.detail === 'object') {
          const d = error.response.data.detail;
          const human = d.message || d.error || d.code || JSON.stringify(d);
          // Conserver l'objet original sous _detailObject pour les consommateurs avancés
          error.response.data._detailObject = d;
          error.response.data.detail = human;
        }
        // 🔒 FNE Lock — intercepteur 423 : afficher un toast clair sur facture verrouillée DGI
        if (error.response?.status === 423) {
          const d = error.response.data?._detailObject || error.response.data?.detail;
          let message = 'Facture verrouillée FNE';
          if (d && typeof d === 'object' && d.code === 'FNE_LOCKED') {
            message = d.message || message;
          } else if (typeof error.response.data?.detail === 'string') {
            message = error.response.data.detail;
          }
          toast.error('Action refusée — Verrou FNE', {
            description: message,
            duration: 6000,
            id: 'fne-locked',
          });
        }
        // RBAC V2 Phase 6 — intercepteur 403 : afficher un toast clair sur permission refusée
        if (error.response?.status === 403) {
          const detail = error.response.data?.detail || '';
          const requestUrl = error.config?.url || '';
          // Ignorer les /ws/ et webhooks (non-critique)
          const ignorePaths = ['/whatsapp/', '/webhook', '/ws/', '/auth/admin/login'];
          if (!ignorePaths.some(p => requestUrl.includes(p))) {
            // Détecter "Permission requise : xxx.yyy"
            const permMatch = typeof detail === 'string' && detail.match(/(?:Permission requise|requise)\s*:?\s*['"]?([a-z_]+\.[a-z_.]+)['"]?/i);
            if (permMatch) {
              toast.error('Accès refusé', {
                description: `Permission manquante : ${permMatch[1]}. Contactez votre administrateur.`,
                duration: 5000,
                id: `rbac-${permMatch[1]}`,  // dédoublonnage
              });
            } else if (typeof detail === 'string' && detail.length > 0 && detail.length < 200) {
              toast.error('Accès refusé', { description: detail, duration: 4000, id: `403-${requestUrl}` });
            }
          }
        }
        if (error.response?.status === 401) {
          const originalRequest = error.config;
          const requestUrl = originalRequest?.url || '';

          // Non-critical: silent fail
          const nonCriticalPaths = ['/whatsapp/', '/webhook', '/ws/', '/analytics/', '/tracking/public/', '/tracking/route/public/'];
          if (nonCriticalPaths.some(p => requestUrl.includes(p))) {
            return Promise.reject(error);
          }

          // SEC-003 : ne pas tenter de refresh sur les endpoints d'auth eux-mêmes (sinon boucle infinie)
          const authPaths = ['/auth/me', '/auth/login', '/auth/admin/login', '/auth/refresh-token', '/auth/logout', '/auth/register'];
          if (authPaths.some(p => requestUrl.includes(p))) {
            return Promise.reject(error);
          }

          // Try refresh once
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            const refreshed = await refreshToken();
            if (refreshed) {
              consecutive401Count = 0;
              return axios(originalRequest);
            }
          }

          consecutive401Count++;
          if (consecutive401Count >= 2) {
            const currentPath = window.location.pathname;
            if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
              handleLogout();
              toast.error('Session expirée', { description: 'Veuillez vous reconnecter' });
              window.location.href = currentPath.startsWith('/admin') ? '/admin/login' : '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );

    // Boot — validate session via cookie (call /auth/me) only if a previous session is hinted
    (async () => {
      cleanupLegacyTokens();
      // SEC-012 : s'assurer qu'on a un cookie CSRF avant tout (au cas où on ferait du POST anonyme)
      if (!readCookie('csrf_token')) {
        try { await axios.get(`${API}/auth/csrf-token`); } catch (_) {}
      }
      const cachedUser = localStorage.getItem('baobab_user');
      if (!cachedUser) {
        // Pas de session précédente → on évite l'appel /auth/me (et la boucle 401)
        setLoading(false);
        return;
      }
      try { setUser(JSON.parse(cachedUser)); setIsAuthenticated(true); } catch (_) {}
      try {
        const me = await axios.get(`${API}/auth/me`);
        if (me?.data) {
          setUser(me.data);
          setIsAuthenticated(true);
          localStorage.setItem('baobab_user', JSON.stringify(me.data));
          scheduleTokenRefresh(60 * 60);
        }
      } catch (e) {
        // Cookie expiré/invalide → nettoyage silencieux
        handleLogout();
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      axios.interceptors.response.eject(interceptor);
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { expiresIn, user: userData } = response.data;

      // Cookie posé automatiquement par le backend (Set-Cookie HttpOnly)
      localStorage.setItem('baobab_user', JSON.stringify(userData));
      if (expiresIn) scheduleTokenRefresh(expiresIn);
      setUser(userData);
      setIsAuthenticated(true);

      // Phase 3 — Track session start (fire-and-forget)
      try {
        const { trackSession } = await import('../utils/analytics');
        trackSession();
      } catch (_) { /* never block login */ }

      return { success: true, role: userData.role };
    } catch (error) {
      const errorDetail = error.response?.data?.detail || '';
      if (errorDetail === 'EMAIL_NOT_VERIFIED') {
        return { success: false, error: 'EMAIL_NOT_VERIFIED', email,
                 message: 'Veuillez valider votre adresse email pour activer votre compte.' };
      }
      if (errorDetail === 'STAFF_USE_ADMIN_LOGIN') {
        return { success: false, error: 'STAFF_USE_ADMIN_LOGIN',
                 message: "Ce compte est un compte staff. Veuillez utiliser l'interface d'administration." };
      }
      return { success: false, error: errorDetail || error.response?.data?.message || 'Erreur de connexion' };
    }
  };

  const loginDriver = async (emailOrPhone, password) => {
    try {
      const payload = emailOrPhone.includes('@')
        ? { email: emailOrPhone, password }
        : { phone: emailOrPhone, password };
      const response = await axios.post(`${API}/driver/auth/login`, payload);
      const { expiresIn, user: userData } = response.data;
      userData.role = 'chauffeur';
      localStorage.setItem('baobab_user', JSON.stringify(userData));
      if (expiresIn) scheduleTokenRefresh(expiresIn);
      setUser(userData);
      setIsAuthenticated(true);
      return { success: true, role: 'chauffeur' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur de connexion' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      if (response.data.requiresVerification) {
        return { success: true, requiresVerification: true,
                 message: response.data.message || "Veuillez vérifier votre email pour activer votre compte." };
      }
      const { user: newUser } = response.data;
      if (newUser) {
        if (newUser.role === 'client') newUser.role = 'expediteur';
        localStorage.setItem('baobab_user', JSON.stringify(newUser));
        setUser(newUser);
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: true, requiresVerification: true,
               message: response.data.message || "Veuillez vérifier votre email pour activer votre compte." };
    } catch (error) {
      const errorCode = error.response?.data?.detail || '';
      let errorMessage = "Une erreur s'est produite lors de l'inscription";
      let errorTitle = "Erreur d'inscription";
      if (errorCode === 'EMAIL_ALREADY_EXISTS') {
        errorTitle = "Email déjà utilisé";
        errorMessage = "Cette adresse email est déjà associée à un compte.";
      } else if (errorCode === 'PHONE_ALREADY_EXISTS') {
        errorTitle = "Numéro déjà utilisé";
        errorMessage = "Ce numéro de téléphone est déjà associé à un compte.";
      } else if (errorCode) {
        errorMessage = errorCode;
      }
      return { success: false, error: errorMessage, errorTitle, errorCode };
    }
  };

  const logout = async () => {
    // Détecter le contexte AVANT de nettoyer l'état (user encore disponible).
    // - Staff/admin : sur /admin/* ou userType='internal' → /admin/login
    // - Chauffeur : sur /driver/* → /driver/login
    // - Sinon : portail client /login
    const path = window.location.pathname || '';
    const userType = user?.userType;
    let redirectTo = '/login';
    if (path.startsWith('/admin') || userType === 'internal') {
      redirectTo = '/admin/login';
    } else if (path.startsWith('/driver') || userType === 'driver') {
      redirectTo = '/driver/login';
    }

    try {
      await axios.post(`${API}/auth/logout`, {}, { timeout: 5000 });
    } catch (err) {
      logger.debug('[logout] Server logout call failed:', err?.message);
    }
    handleLogout();
    // Rediriger explicitement pour éviter que ProtectedRoute
    // renvoie systématiquement vers le portail client.
    window.location.href = redirectTo;
  };

  const updateUser = (updatedData) => {
    const newUserData = { ...user, ...updatedData };
    setUser(newUserData);
    localStorage.setItem('baobab_user', JSON.stringify(newUserData));
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/profile`);
      if (response.data) {
        setUser(response.data);
        localStorage.setItem('baobab_user', JSON.stringify(response.data));
        return response.data;
      }
    } catch (error) {
      logger.debug('Error refreshing user:', error?.message);
    }
    return null;
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    loginDriver,
    register,
    logout,
    updateUser,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
